import { useCallback, useEffect, useId, useState } from 'react';
import { PARALLEL_CALLS_MAX, PARALLEL_CALLS_MIN, PARALLEL_CALLS_WARNING_THRESHOLD } from '@autodialer/shared';
import type { CampaignMode } from '@autodialer/shared';

import { ApiError } from '../../api/client';
import {
  campaignsApi,
  queueMusicApi,
  type Campaign,
  type CampaignField,
  type ModeChangePreview,
} from '../../api/campaigns';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  FormField,
  Input,
  LinkButton,
  Section,
  Select,
  Slider,
  StatusChip,
  Toggle,
} from '../../components';
import { SipConfigSection } from './SipConfigSection';
import { useI18n } from '../../i18n';
import { useSession } from '../../auth/session-context';

/** Länder, die im Zielmarkt tatsächlich gebraucht werden. */
const COUNTRIES = ['DE', 'AT', 'CH', 'TR', 'GB', 'US'] as const;

/**
 * Kampagneneinstellungen.
 *
 * Gegliedert nach dem, was ein Kundenadmin im Kopf hat: Allgemeines, wie die
 * Arbeit verteilt wird, der Dialer, die Telefonie, eingehende Anrufe, die
 * Warteschleife, Aufnahmen und die eigenen Felder. Alles, was selten gebraucht
 * wird oder technisch ist, liegt unter „Erweiterte Einstellungen".
 *
 * Jede Einstellung, die sich nicht von selbst erklärt, hat ein Fragezeichen
 * mit ein bis drei Sätzen in Alltagssprache.
 */
export function SettingsTab({ campaign, onChange }: { campaign: Campaign; onChange: () => void }): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();
  const nameId = useId();
  const beschreibungId = useId();
  const landId = useId();
  const feldId = useId();
  const parallelId = useId();

  const [name, setName] = useState(campaign.name);
  const [description, setDescription] = useState(campaign.description ?? '');
  const [fields, setFields] = useState<CampaignField[]>([]);
  const [newField, setNewField] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [modusWechsel, setModusWechsel] = useState<{ ziel: CampaignMode; vorschau: ModeChangePreview } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadFields = useCallback(async () => {
    try {
      const response = await campaignsApi.listFields(campaign.id);
      setFields(response.fields);
    } catch (caught) {
      handleApiError(caught);
    }
  }, [campaign.id, handleApiError]);

  useEffect(() => {
    void loadFields();
  }, [loadFields]);

  const run = async (action: () => Promise<unknown>, done?: string): Promise<void> => {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      await action();
      onChange();
      await loadFields();
      setMessage(done ?? t('campaign.settings.saved'));
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  /** Erst die Folgen zeigen, dann fragen - und zwar in einem eigenen Dialog. */
  const modusWaehlen = (ziel: CampaignMode): void => {
    if (ziel === campaign.campaignMode) {
      return;
    }
    void (async () => {
      setError(null);
      try {
        const { preview } = await campaignsApi.previewMode(campaign.id, ziel);
        if (!preview.possible) {
          setError(tError(preview.blockedReason));
          return;
        }
        setModusWechsel({ ziel, vorschau: preview });
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      }
    })();
  };

  const gestoppt = !campaign.isActive;
  /** Kritische Einstellungen nur bei gestoppter Kampagne (03_campaigns.md). */
  const kritischGesperrt = !gestoppt || busy;

  return (
    <div className="steps">
      {error ? <Alert tone="error">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}
      {!gestoppt ? <Alert tone="info">{t('campaign.stopFirst')}</Alert> : null}

      <Card>
        <Section title={t('campaign.section.general')} description={t('campaign.section.generalHint')}>
          <FormField label={t('campaigns.name')} htmlFor={nameId}>
            <Input id={nameId} value={name} onChange={setName} />
          </FormField>
          <FormField label={t('campaigns.description')} htmlFor={beschreibungId}>
            <Input id={beschreibungId} value={description} onChange={setDescription} />
          </FormField>
          <FormField label={t('campaign.settings.country')} htmlFor={landId} help={t('campaign.help.country')}>
            <Select
              id={landId}
              value={campaign.defaultCountryCode}
              disabled={kritischGesperrt}
              options={COUNTRIES.map((country) => ({ value: country, label: country }))}
              onChange={(wert) => void run(() => campaignsApi.update(campaign.id, { defaultCountryCode: wert }))}
            />
          </FormField>
          <div className="row-actions">
            <Button
              variant="primary"
              disabled={busy}
              onClick={() =>
                void run(() => campaignsApi.update(campaign.id, { name, description: description || null }))
              }
            >
              {t('action.save')}
            </Button>
          </div>
        </Section>
      </Card>

      <Card>
        <Section title={t('campaign.section.callers')} description={t('campaign.section.callersHint')}>
          <FormField label={t('campaign.settings.mode')} help={t('campaign.help.mode')}>
            <Select
              value={campaign.campaignMode}
              disabled={kritischGesperrt}
              options={[
                { value: 'pool' as CampaignMode, label: t('campaigns.mode.pool') },
                { value: 'assigned' as CampaignMode, label: t('campaigns.mode.assigned') },
              ]}
              onChange={modusWaehlen}
            />
          </FormField>
        </Section>
      </Card>

      <SipConfigSection campaignId={campaign.id} />

      <Card>
        <Section title={t('campaign.section.data')} description={t('campaign.section.dataHint')}>
          <div className="form form-inline">
            <FormField
              label={t('campaign.settings.fieldName')}
              htmlFor={feldId}
              help={t('campaign.help.fields')}
            >
              <Input id={feldId} value={newField} onChange={setNewField} />
            </FormField>
            <Button
              disabled={busy || newField.trim() === ''}
              onClick={() =>
                void run(async () => {
                  await campaignsApi.createField(campaign.id, newField.trim());
                  setNewField('');
                })
              }
            >
              {t('campaign.settings.addField')}
            </Button>
          </div>

          {fields.length > 0 ? (
            <ul className="list">
              {fields.map((field) => (
                <li key={field.id} className="list-row">
                  <div className="list-row-text">
                    <strong>{field.fieldName}</strong>
                    {field.requiredBeforeCompletion ? (
                      <span>
                        <StatusChip tone="info">{t('contact.requiredBeforeCall')}</StatusChip>
                      </span>
                    ) : null}
                  </div>
                  <div className="row-actions">
                    <Checkbox
                      checked={field.requiredBeforeCompletion}
                      disabled={busy}
                      label={t('campaign.settings.required')}
                      help={t('campaign.help.required')}
                      onChange={(checked) =>
                        void run(() =>
                          campaignsApi.updateField(campaign.id, field.id, {
                            requiredBeforeCompletion: checked,
                          }),
                        )
                      }
                    />
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void run(() => campaignsApi.removeField(campaign.id, field.id))}
                    >
                      {t('campaign.settings.removeField')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </Section>
      </Card>

      <Card>
        <Section title={t('campaign.section.advanced')}>
          <LinkButton onClick={() => setAdvanced((current) => !current)}>
            {advanced ? t('action.close') : t('campaign.section.advanced')}
          </LinkButton>

          {advanced ? (
            <div className="form">
              <Section title={t('campaign.section.dialer')} description={t('campaign.section.dialerHint')}>
                <FormField
                  label={t('campaign.settings.parallelCalls')}
                  htmlFor={parallelId}
                  help={t('campaign.help.parallelCalls')}
                >
                  <Slider
                    id={parallelId}
                    min={PARALLEL_CALLS_MIN}
                    max={PARALLEL_CALLS_MAX}
                    value={campaign.parallelCalls}
                    disabled={kritischGesperrt}
                    onChange={(wert) => void run(() => campaignsApi.update(campaign.id, { parallelCalls: wert }))}
                  />
                </FormField>
                {campaign.parallelCalls >= PARALLEL_CALLS_WARNING_THRESHOLD ? (
                  <Alert tone="warning">{t('campaign.settings.parallelWarning')}</Alert>
                ) : null}

                <FormField label={t('campaign.settings.parallelMode')} help={t('campaign.help.parallelMode')}>
                  <Select
                    value={campaign.parallelMode}
                    disabled={kritischGesperrt}
                    options={[
                      { value: 'per_caller' as const, label: t('campaign.settings.parallelMode.per_caller') },
                      { value: 'global' as const, label: t('campaign.settings.parallelMode.global') },
                    ]}
                    onChange={(wert) => void run(() => campaignsApi.update(campaign.id, { parallelMode: wert }))}
                  />
                </FormField>

                <FormField label={t('campaign.settings.retry')} help={t('campaign.help.retry')}>
                  <input
                    type="number"
                    min={0}
                    max={20}
                    value={campaign.retryCount24h}
                    disabled={busy}
                    onChange={(event) =>
                      void run(() =>
                        campaignsApi.update(campaign.id, { retryCount24h: Number(event.target.value) }),
                      )
                    }
                  />
                </FormField>

                <Toggle
                  checked={campaign.mailboxDetectionEnabled}
                  disabled={busy}
                  label={t('campaign.settings.mailbox')}
                  help={t('campaign.help.mailbox')}
                  onChange={(checked) =>
                    void run(() => campaignsApi.update(campaign.id, { mailboxDetectionEnabled: checked }))
                  }
                />
              </Section>

              <Section title={t('campaign.section.inbound')} description={t('campaign.section.inboundHint')}>
                <Toggle
                  checked={campaign.inboundEnabled}
                  disabled={busy}
                  label={t('campaign.settings.inbound')}
                  help={t('campaign.help.inbound')}
                  onChange={(checked) =>
                    void run(() => campaignsApi.update(campaign.id, { inboundEnabled: checked }))
                  }
                />
              </Section>

              <Section title={t('campaign.section.queue')} description={t('campaign.section.queueHint')}>
                <Toggle
                  checked={campaign.queueEnabled}
                  disabled={busy}
                  label={t('campaign.settings.queue')}
                  help={t('campaign.help.queue')}
                  onChange={(checked) =>
                    void run(() => campaignsApi.update(campaign.id, { queueEnabled: checked }))
                  }
                />
                {campaign.queueEnabled ? (
                  <>
                    <FormField label={t('campaign.settings.queueWait')} help={t('campaign.help.queueWait')}>
                      <input
                        type="number"
                        min={1}
                        max={600}
                        value={campaign.queueMaxWaitSeconds}
                        disabled={busy}
                        onChange={(event) =>
                          void run(() =>
                            campaignsApi.update(campaign.id, {
                              queueMaxWaitSeconds: Number(event.target.value),
                            }),
                          )
                        }
                      />
                    </FormField>

                    <Wartemusik campaign={campaign} onChange={onChange} gesperrt={kritischGesperrt} />
                  </>
                ) : null}
              </Section>

              <Section title={t('campaign.section.recording')} description={t('campaign.section.recordingHint')}>
                <Toggle
                  checked={campaign.recordingDefault}
                  disabled={busy}
                  label={t('campaign.settings.recording')}
                  help={t('campaign.help.recording')}
                  onChange={(checked) =>
                    void run(() => campaignsApi.update(campaign.id, { recordingDefault: checked }))
                  }
                />
              </Section>
            </div>
          ) : null}
        </Section>
      </Card>

      {modusWechsel ? (
        <ConfirmDialog
          title={t('dialog.modeChange')}
          message={t('campaign.settings.modePreview', {
            contacts: modusWechsel.vorschau.affectedContacts,
            callers: modusWechsel.vorschau.assignedCallers,
          })}
          confirmLabel={t('campaign.settings.changeMode')}
          tone="primary"
          busy={busy}
          onCancel={() => setModusWechsel(null)}
          onConfirm={() => {
            const ziel = modusWechsel.ziel;
            setModusWechsel(null);
            void run(() => campaignsApi.changeMode(campaign.id, ziel));
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Wartemusik der Kampagne.
 *
 * Eine Datei, mehr nicht - keine Medienbibliothek (06_telephony_sip_inbound.md).
 * Der Dateiname aus dem Browser wird serverseitig nie als Pfad verwendet.
 */
function Wartemusik({
  campaign,
  onChange,
  gesperrt,
}: {
  campaign: Campaign;
  onChange: () => void;
  gesperrt: boolean;
}): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = (aktion: () => Promise<unknown>, erfolg?: string): void => {
    void (async () => {
      setError(null);
      setMessage(null);
      setBusy(true);
      try {
        await aktion();
        onChange();
        if (erfolg) {
          setMessage(erfolg);
        }
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Section title={t('queueMusic.title')} description={t('queueMusic.hint')}>
      {error ? <Alert tone="error">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}

      {campaign.queueMusicFilename ? (
        <div className="row-actions">
          <StatusChip tone="info">
            {t('queueMusic.current')}: {campaign.queueMusicFilename}
          </StatusChip>
          <Button
            variant="ghost"
            disabled={busy || gesperrt}
            onClick={() => run(() => queueMusicApi.remove(campaign.id))}
          >
            {t('queueMusic.remove')}
          </Button>
        </div>
      ) : (
        <p className="field-hint">{t('queueMusic.none')}</p>
      )}

      <FormField label={t('queueMusic.upload')}>
        <input
          type="file"
          accept="audio/wav,audio/mpeg,.wav,.mp3"
          disabled={busy || gesperrt}
          onChange={(event) => {
            const datei = event.target.files?.[0];
            if (datei) {
              run(() => queueMusicApi.upload(campaign.id, datei), t('queueMusic.saved'));
            }
            event.target.value = '';
          }}
        />
      </FormField>
    </Section>
  );
}
